import { useState, useEffect, useCallback } from 'react';
import { leadsAPI } from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Phone, Mail, MapPin } from 'lucide-react';

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchLeads = useCallback(async () => {
    try {
      const data = await leadsAPI.getAll();
      setLeads(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching leads:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const filterLeads = useCallback(() => {
    let filtered = leads;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(lead => lead.status === statusFilter);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(lead => 
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.phone.includes(searchTerm)
      );
    }
    
    setFilteredLeads(filtered);
  }, [leads, statusFilter, searchTerm]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    filterLeads();
  }, [filterLeads]);

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await leadsAPI.update(leadId, { status: newStatus });
      fetchLeads();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error updating lead:', error);
      }
    }
  };

  if (loading) {
    return <div className="p-8"><p className="font-manrope text-gray-600">Loading leads...</p></div>;
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-barlow text-4xl font-bold uppercase mb-2">Lead Management</h1>
        <p className="font-manrope text-gray-600">Track and manage all your leads</p>
      </div>

      <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000] mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              placeholder="Search by name, city, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-2 border-black"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="border-2 border-black">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="qualified">Qualified</SelectItem>
                <SelectItem value="booked">Booked</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-right">
              <span className="font-barlow text-xl font-bold">{filteredLeads.length}</span>
              <span className="font-manrope text-gray-600 ml-2">leads found</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {filteredLeads.map((lead) => (
          <Card key={lead.id} className="border-2 border-black shadow-[2px_2px_0px_0px_#000]">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="md:col-span-2">
                  <h3 className="font-barlow text-xl font-bold mb-2">{lead.name}</h3>
                  <div className="space-y-2 text-sm font-manrope text-gray-600">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <a href={`tel:${lead.phone}`} className="hover:text-primary">{lead.phone}</a>
                    </div>
                    {lead.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        <a href={`mailto:${lead.email}`} className="hover:text-primary">{lead.email}</a>
                      </div>
                    )}
                    {lead.city && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <span>{lead.city}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <p className="text-sm font-manrope text-gray-600 mb-2">Roof Type</p>
                  <p className="font-semibold">{lead.roof_type || 'Not specified'}</p>
                  {lead.square_footage && (
                    <>
                      <p className="text-sm font-manrope text-gray-600 mt-3 mb-2">Square Footage</p>
                      <p className="font-semibold">{lead.square_footage} sq ft</p>
                    </>
                  )}
                </div>
                
                <div>
                  <p className="text-sm font-manrope text-gray-600 mb-2">Status</p>
                  <Select value={lead.status} onValueChange={(value) => updateLeadStatus(lead.id, value)}>
                    <SelectTrigger className="border-2 border-black">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="qualified">Qualified</SelectItem>
                      <SelectItem value="booked">Booked</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs font-manrope text-gray-500 mt-2">
                    Source: {lead.source}
                  </p>
                </div>
              </div>
              
              {lead.message && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-manrope text-gray-600 mb-1">Message:</p>
                  <p className="text-sm">{lead.message}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        
        {filteredLeads.length === 0 && (
          <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000]">
            <CardContent className="pt-6 text-center">
              <p className="font-manrope text-gray-600">No leads found matching your criteria</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Leads;
